import { useIsMutating, useQuery } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';

import type {
  Persona,
  ResearchAnalysisStance,
  ResearchAnalysisTheme,
  ResearchAnalysisTone,
  ResearchRoomMessage,
} from '@repo/contracts';
import { estimateResearchAnalysisCredits } from '@repo/contracts/credits';
import { Tooltip, TooltipProvider } from '@repo/ui/tooltip';

import { getRoomForCapture, roomKeys } from '../api/research-rooms';
import { CreditDot } from '../credits/credit-chip';
import { PersonaAvatar } from '../persona/persona-avatar';
import { usePersonas } from '../persona/use-personas';
import { reportPath } from '../research-report/use-report';
import { useResearchRoomStore } from '../store/research-room-store';
import {
  answeredRounds,
  milestoneRounds,
  moodSeries,
  newThemeIds,
} from './analysis';
import { FlotoMark } from './room-messages';
import { analysisMutationKey, useRoomAnalysis } from './use-room-analysis';

/** The card's width on the board, and its distance from the screen it sits
 *  beside — inside the gap the board leaves between screens. */
export const ANALYSIS_WIDTH = 480;
export const ANALYSIS_GAP = 40;

/** Below this zoom only the headline and the grid's dots stay: the words
 *  would be specks. */
const COMPACT_BELOW = 0.32;

const TONE_FILL: Record<ResearchAnalysisTone, string> = {
  friction: 'bg-brand',
  mixed: 'bg-warning',
  praise: 'bg-success-ink',
};
const TONE_RING: Record<ResearchAnalysisTone, string> = {
  friction: 'ring-brand',
  mixed: 'ring-warning',
  praise: 'ring-success-ink',
};
const TONE_LABEL: Record<ResearchAnalysisTone, string> = {
  friction: 'A problem',
  mixed: 'Contested',
  praise: 'Works well',
};
const STANCE_LABEL: Record<ResearchAnalysisStance['stance'], string> = {
  agree: 'agrees',
  disagree: 'disagrees',
  mixed: 'is torn',
};

function first(name: string): string {
  return name.split(' ')[0] ?? name;
}

/** One cell: how a persona stands on a theme. Filled agrees, hollow
 *  disagrees, half is torn — coloured by what kind of theme it is. */
function StanceDot({
  stance,
  tone,
}: {
  stance: ResearchAnalysisStance['stance'];
  tone: ResearchAnalysisTone;
}) {
  if (stance === 'agree') {
    return <span className={`block size-3 rounded-full ${TONE_FILL[tone]}`} />;
  }
  if (stance === 'disagree') {
    return (
      <span
        className={`block size-3 rounded-full bg-surface ring-2 ring-inset ${TONE_RING[tone]}`}
      />
    );
  }
  return (
    <span
      className={`block size-3 overflow-hidden rounded-full bg-surface ring-2 ring-inset ${TONE_RING[tone]}`}
    >
      <span className={`block h-full w-1/2 ${TONE_FILL[tone]}`} />
    </span>
  );
}

function ThemeRow({
  compact,
  fresh,
  onCite,
  personas,
  theme,
}: {
  compact: boolean;
  fresh: boolean;
  onCite: (stance: ResearchAnalysisStance) => void;
  personas: Persona[];
  theme: ResearchAnalysisTheme;
}) {
  return (
    <li className="flex items-center gap-3 py-2">
      <span
        aria-label={TONE_LABEL[theme.tone]}
        className={`size-2 shrink-0 rounded-full ${TONE_FILL[theme.tone]}`}
        role="img"
      />
      {compact ? null : (
        <p className="min-w-0 flex-1 text-caption leading-snug text-text-primary">
          {theme.claim}
          {fresh ? (
            <span className="ml-1.5 rounded-pill bg-idea-soft px-1.5 py-0.5 align-middle text-badge font-semibold text-text-primary">
              New
            </span>
          ) : null}
        </p>
      )}
      <div className="flex shrink-0 gap-1">
        {personas.map((persona) => {
          const stance = theme.stances.find((s) => s.personaId === persona.id);
          if (!stance) {
            return (
              <span
                aria-label={`${persona.name}: not heard`}
                className="flex size-7 items-center justify-center"
                key={persona.id}
                role="img"
              >
                <span className="block h-px w-2 bg-hairline-alt" />
              </span>
            );
          }
          return (
            <Tooltip
              key={persona.id}
              label={`${first(persona.name)} ${STANCE_LABEL[stance.stance]}: “${stance.quote}”`}
            >
              <button
                aria-label={`${persona.name} ${STANCE_LABEL[stance.stance]}: ${stance.quote}`}
                className="flex size-7 cursor-pointer items-center justify-center rounded-button transition hover:bg-surface-hover"
                onClick={() => {
                  onCite(stance);
                }}
                type="button"
              >
                <StanceDot stance={stance.stance} tone={theme.tone} />
              </button>
            </Tooltip>
          );
        })}
      </div>
    </li>
  );
}

const SPARK_WIDTH = 220;
const SPARK_HEIGHT = 22;

/** One persona's mood over the session: a line through their answers'
 *  sentiment, round by round, with Floto's milestones marked under it. */
function MoodLine({
  milestones,
  onCite,
  persona,
  points,
  rounds,
}: {
  milestones: Set<number>;
  onCite: (messageId: string) => void;
  persona: Persona;
  points: { messageId: string; round: number; value: number }[];
  rounds: number[];
}) {
  const step = rounds.length > 1 ? SPARK_WIDTH / (rounds.length - 1) : 0;
  const xOf = (round: number) =>
    rounds.length > 1 ? rounds.indexOf(round) * step : SPARK_WIDTH / 2;
  const yOf = (value: number) => ((1 - value) / 2) * (SPARK_HEIGHT - 6) + 3;
  const path = points
    .map((p) => `${String(xOf(p.round))},${String(yOf(p.value))}`)
    .join(' ');

  return (
    <li className="flex items-center gap-3">
      <span className="w-14 shrink-0 truncate text-tag text-text-secondary">
        {first(persona.name)}
      </span>
      <svg
        aria-label={`${persona.name}'s mood over the session`}
        className="overflow-visible"
        height={SPARK_HEIGHT}
        role="img"
        width={SPARK_WIDTH}
      >
        <line
          className="stroke-hairline"
          x1={0}
          x2={SPARK_WIDTH}
          y1={yOf(0)}
          y2={yOf(0)}
        />
        {rounds
          .filter((round) => milestones.has(round))
          .map((round) => (
            <line
              className="stroke-idea"
              key={round}
              strokeDasharray="2 2"
              x1={xOf(round)}
              x2={xOf(round)}
              y1={0}
              y2={SPARK_HEIGHT}
            />
          ))}
        {points.length > 1 ? (
          <polyline
            className="fill-none stroke-text-secondary"
            points={path}
            strokeLinejoin="round"
            strokeWidth={1.5}
          />
        ) : null}
        {points.map((p) => (
          <circle
            className={[
              'cursor-pointer transition hover:opacity-70',
              p.value > 0
                ? 'fill-success-ink'
                : p.value < 0
                  ? 'fill-brand'
                  : 'fill-text-muted',
            ].join(' ')}
            cx={xOf(p.round)}
            cy={yOf(p.value)}
            key={p.messageId}
            onClick={() => {
              onCite(p.messageId);
            }}
            r={3.5}
          >
            <title>{`Round ${String(p.round)}`}</title>
          </circle>
        ))}
      </svg>
    </li>
  );
}

/**
 * Floto's write-up of a room, on the board beside its screen: where the room
 * stands in a sentence, the themes it keeps returning to and where each
 * person stands on them, their mood round by round, and what the goal still
 * needs.
 *
 * Everything links back: a dot opens the answer it came from in the thread
 * and lights up the part of the screen it was about. The words are Floto's,
 * written on milestones or on request; the mood lines are plain code over
 * the room's sentiments, and move every round for free.
 */
export function AnalysisCard({
  captureId,
  projectId,
  zoom,
}: {
  captureId: string;
  projectId: string;
  zoom: number;
}) {
  const { data: view } = useQuery({
    queryFn: () => getRoomForCapture(captureId),
    queryKey: roomKeys.forCapture(captureId),
  });
  const { personas: panel } = usePersonas({ projectId });
  const showSpotlight = useResearchRoomStore((s) => s.showSpotlight);
  const showMessage = useResearchRoomStore((s) => s.showMessage);
  const seen = useResearchRoomStore((s) =>
    view ? s.seenAnalysis[view.room.id] : undefined,
  );
  const { analyse, error, shortfall } = useRoomAnalysis(
    captureId,
    view ?? null,
  );
  const writing =
    useIsMutating({ mutationKey: analysisMutationKey(view?.room.id) }) > 0;

  const room = view?.room;
  const analysis = room?.analysis;
  if (!view || !room || !analysis) return null;

  const messages = view.messages;
  const byId = new Map(panel.map((persona) => [persona.id, persona]));
  const seated = room.personaIds.flatMap((id) => byId.get(id) ?? []);
  const compact = zoom < COMPACT_BELOW;
  const fresh = newThemeIds(analysis, seen);
  const rounds = answeredRounds(messages);
  const moods = moodSeries(messages, room.personaIds);
  const milestones = new Set(milestoneRounds(messages));
  const thinking = room.status === 'thinking';

  /** A dot's answer: shown in the thread, and lit on the screen. */
  const cite = (messageId: string) => {
    const message: ResearchRoomMessage | undefined = messages.find(
      (m) => m.id === messageId,
    );
    if (!message) return;
    showMessage(message.id);
    if (message.focus) {
      showSpotlight({
        auto: false,
        captureId,
        focus: message.focus,
        speaker:
          message.speakerKind === 'persona' && message.speakerId
            ? (byId.get(message.speakerId) ?? null)
            : null,
      });
    }
  };

  return (
    <TooltipProvider>
      <article
        aria-label="Floto's write-up of the session"
        className="absolute top-0 cursor-default rounded-card bg-surface p-5 shadow-floating ring-1 ring-hairline duration-500 animate-in fade-in slide-in-from-left-2 motion-reduce:animate-none"
        data-analysis-card={captureId}
        // The board pans on a press anywhere on it; the card is for reading
        // and clicking, so a press here stays here.
        onPointerDown={(event) => {
          event.stopPropagation();
        }}
        style={{
          left: `calc(100% + ${String(ANALYSIS_GAP)}px)`,
          width: ANALYSIS_WIDTH,
        }}
      >
        <header className="flex items-center gap-2">
          <FlotoMark />
          <p className="flex-1 text-ui">
            <span className="font-semibold text-text-primary">Floto</span>
            <span className="text-text-muted"> · Session write-up</span>
          </p>
          {room.endedAt ? (
            <Link
              className="text-caption font-semibold text-brand underline-offset-2 hover:underline"
              to={reportPath(room)}
            >
              Read the report →
            </Link>
          ) : (
            <Tooltip label="Write it up again with what the room has said since">
              <button
                aria-label="Refresh the write-up"
                className="flex h-7 cursor-pointer items-center gap-1.5 rounded-button px-2 text-tag text-text-secondary ring-1 ring-hairline transition hover:bg-surface-hover hover:text-text-primary disabled:cursor-default disabled:opacity-50"
                disabled={writing || thinking}
                onClick={() => {
                  analyse('manual');
                }}
                type="button"
              >
                <RefreshCw
                  aria-hidden="true"
                  className={`size-3.5 ${writing ? 'animate-spin motion-reduce:animate-none' : ''}`}
                />
                <CreditDot />~
                {estimateResearchAnalysisCredits(analysis.credits)}
              </button>
            </Tooltip>
          )}
        </header>
        <p className="mt-1 text-tag text-text-muted tabular-nums">
          {writing
            ? 'Floto is writing this up…'
            : `After round ${String(analysis.round)}${
                analysis.credits
                  ? ` · ${String(analysis.credits)} credit${analysis.credits === 1 ? '' : 's'}`
                  : ''
              }`}
        </p>
        {shortfall ? (
          <p className="mt-2 text-caption text-critical" role="alert">
            Not enough credits to write it up again — it needs about{' '}
            {shortfall.required}.
          </p>
        ) : error ? (
          <p className="mt-2 text-caption text-critical" role="alert">
            {error}
          </p>
        ) : null}

        <h3
          className={[
            '!m-0 mt-3 !font-semibold !text-text-primary',
            compact ? '!text-card-title' : '!text-ui-lg !leading-snug',
          ].join(' ')}
        >
          {analysis.headline}
        </h3>

        {analysis.themes.length > 0 ? (
          <section className="mt-4">
            <div className="flex items-end gap-3 border-b border-hairline pb-2">
              <p className="flex-1 text-small-label text-text-muted uppercase">
                {compact ? '' : 'Where they stand'}
              </p>
              <div className="flex shrink-0 gap-1">
                {seated.map((persona) => (
                  <Tooltip key={persona.id} label={persona.name}>
                    <span className="flex size-7 items-center justify-center">
                      <PersonaAvatar persona={persona} size="sm" />
                    </span>
                  </Tooltip>
                ))}
              </div>
            </div>
            <ul className="divide-y divide-hairline">
              {analysis.themes.map((theme) => (
                <ThemeRow
                  compact={compact}
                  fresh={fresh.has(theme.id)}
                  key={theme.id}
                  onCite={(stance) => {
                    cite(stance.messageId);
                  }}
                  personas={seated}
                  theme={theme}
                />
              ))}
            </ul>
            {compact ? null : (
              <p className="mt-1 text-tag text-text-muted">
                Filled agrees · hollow disagrees · half is torn · click one to
                see what they said
              </p>
            )}
          </section>
        ) : null}

        {!compact && rounds.length > 1 ? (
          <section className="mt-5">
            <p className="text-small-label text-text-muted uppercase">
              Mood, round by round
            </p>
            <ul className="mt-2 space-y-1.5">
              {seated.map((persona) => (
                <MoodLine
                  key={persona.id}
                  milestones={milestones}
                  onCite={cite}
                  persona={persona}
                  points={moods.get(persona.id) ?? []}
                  rounds={rounds}
                />
              ))}
            </ul>
          </section>
        ) : null}

        {!compact && analysis.open.length > 0 && !room.endedAt ? (
          <section className="mt-5">
            <p className="text-small-label text-text-muted uppercase">
              Still to explore
            </p>
            <ul className="mt-2 flex flex-wrap gap-1.5">
              {analysis.open.map((topic) => (
                <li
                  className="rounded-pill bg-warm px-2.5 py-1 text-caption text-text-secondary"
                  key={topic}
                >
                  {topic}
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </article>
    </TooltipProvider>
  );
}
